import {
    Button,
    Stack,
    TextField,
    Typography,
    Paper,
    Box,
    Divider,
    IconButton,
    InputAdornment,
    useTheme, // Added useTheme for color access
} from '@mui/material';
import { useState } from 'react';
import { loginAPI } from '../../services/api';
import { saveAuth } from '../../utils/storage';
import { useNavigate } from 'react-router-dom';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import DriveEtaIcon from '@mui/icons-material/DriveEta'; // Added a relevant icon

const LoginForm = () => {
    const navigate = useNavigate();
    const theme = useTheme(); // Initialize theme hook
    
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false); // Added loading state
    const [showPassword, setShowPassword] = useState(false);

    const handleLogin = async () => {
        setLoading(true);
        setError('');
        try {
            const res = await loginAPI({ email, password });
            saveAuth(res.token, res.user);
            navigate('/home');
        } catch (err: any) {
            setError(err.message || 'Login failed. Please check your credentials.');
        } finally {
            setLoading(false);
        }
    };

    const handleGuestLogin = async () => {
        setLoading(true);
        setError('');
        try {
            const res = await loginAPI({ email: 'guest@cardealer.com', password: '' });
            saveAuth(res.token, res.user);
            navigate('/home');
        } catch (err: any) {
            setError('Guest login failed. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Paper
            elevation={8} // Higher elevation for a more prominent look
            sx={{
                padding: { xs: 3, sm: 5 }, // Adaptive padding
                maxWidth: 400, // Slightly tighter max width for focus
                margin: 'auto',
                borderRadius: 3, // Increased border radius for softness
                boxShadow: theme.shadows[10], // Use MUI shadows for better integration
                border: `1px solid ${theme.palette.divider}`, // Subtle border
            }}
        >
            <Stack spacing={3}>
                <Box textAlign="center" mb={2}>
                    <DriveEtaIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                    <Typography 
                        variant="h5" 
                        fontWeight={700} 
                        color="primary.dark"
                    >
                        WorkHorse Member Login
                    </Typography>
                    <Typography variant="body1" color="text.secondary" mt={1}>
                        Enter your details to sign in
                    </Typography>
                </Box>
                
                <Divider />

                {error && (
                    <Box sx={{ 
                        p: 1.5, 
                        bgcolor: 'error.main', 
                        color: 'white', 
                        borderRadius: 1, 
                        textAlign: 'center' 
                    }}>
                        <Typography variant="body2" fontWeight={500}>
                            {error}
                        </Typography>
                    </Box>
                )}

                <Stack spacing={2}>
                    {/* Email Field */}
                    <TextField
                        fullWidth
                        label="Email Address"
                        type="email"
                        variant="outlined"
                        placeholder="e.g., john.doe@dealer.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={loading}
                    />

                    {/* Password Field */}
                    <TextField
                        fullWidth
                        label="Password"
                        type={showPassword ? 'text' : 'password'}
                        variant="outlined"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        disabled={loading}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={() => setShowPassword((prev) => !prev)}
                                        edge="end"
                                        disabled={loading}
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Stack>
                
                {/* Primary Login Button */}
                <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={handleLogin}
                    disabled={loading || !email || !password}
                    sx={{ 
                        textTransform: 'uppercase', 
                        fontWeight: 700,
                        mt: 1, // extra space after inputs
                        py: 1.5,
                    }}
                >
                    {loading ? 'Logging In...' : 'Sign In'}
                </Button>

                <Divider sx={{ my: 2 }}>
                    <Typography variant="body2" color="text.disabled">
                        OR
                    </Typography>
                </Divider>

                {/* Guest Login Button */}
                <Button
                    fullWidth
                    variant="outlined"
                    color="secondary" // Use secondary color for contrast/alternative action
                    size="large"
                    onClick={handleGuestLogin}
                    disabled={loading}
                    sx={{ 
                        textTransform: 'none', 
                        fontWeight: 600,
                        py: 1.5,
                        '&:hover': {
                            backgroundColor: 'secondary.light',
                            color: 'white',
                        }
                    }}
                >
                    Continue as Guest
                </Button>
            </Stack>
        </Paper>
    );
};

export default LoginForm;